/**
 * Tailwind classes for the desktop nav rails: the workspace rail, and whichever section rail
 * is mounted beside it.
 *
 * Both rails collapse together on one setting, so a single width applies to both, and the
 * section rail always starts at the workspace rail's right edge.
 *
 * Every class is written out as a literal so Tailwind's scanner picks it up; the rails and the
 * content padding are derived from the same table, which keeps them from drifting apart.
 *
 * Widths are 14rem (`56`) expanded and 3.5rem (`14`) collapsed to icons.
 */
export const SIDEBAR_WIDTH_CLASS = {
  expanded: 'lg:w-56',
  collapsed: 'lg:w-14'
} as const;

/** The workspace rail is always flush with the viewport edge. */
export const WORKSPACE_SIDEBAR_LEFT_CLASS = 'lg:left-0';

/**
 * Stacking order for the rails. The workspace rail sits above the section rail so the popovers
 * it anchors — the dialer, the profile menu — are not clipped behind the rail to its right.
 *
 * Both stay below `z-30`, which is where the app's overlay layer starts: toasts, modals and
 * drawers all render at `z-30` or above, and a fixed rail at `z-40` painted over the top of
 * them.
 */
export const WORKSPACE_SIDEBAR_Z_CLASS = 'lg:z-20';
export const SUB_SIDEBAR_Z_CLASS = 'lg:z-10';

/** Where the section rail starts, i.e. the right edge of the workspace rail. */
export const SUB_SIDEBAR_LEFT_CLASS = {
  expanded: 'lg:left-56',
  collapsed: 'lg:left-14'
} as const;

/**
 * Padding that clears the rails, keyed by their combined width in Tailwind spacing units.
 *
 * `pl-112` is not on Tailwind's named scale, but v4 generates spacing utilities dynamically from
 * `--spacing`, so it resolves to 28rem — the width of two expanded rails.
 */
const CONTENT_OFFSET_CLASS: Record<number, string> = {
  14: 'lg:pl-14',
  28: 'lg:pl-28',
  56: 'lg:pl-56',
  112: 'lg:pl-112'
};

export const desktopContentOffsetClass = ({
  collapsed,
  subRailVisible
}: {
  collapsed: boolean;
  subRailVisible: boolean;
}): string => {
  const railWidth = collapsed ? 14 : 56;

  return CONTENT_OFFSET_CLASS[railWidth * (subRailVisible ? 2 : 1)];
};

/**
 * Where each band of pinned chrome comes to rest, top to bottom. Nothing in the chrome scrolls
 * away: on a narrow view the top bar holds the only way into the navigation, and the page header
 * bar under it is the only thing naming who or what you are looking at.
 *
 * The bands stack, so each level clears every level above it. The top bar is 2rem tall and, on the
 * dropdown navigation, `lg:hidden`, which is why every level below it drops that 2rem back above
 * `lg`. The menu navigation keeps the bar up at every width, so `menu-nav:` puts the 2rem back.
 *
 * Written out as literals so Tailwind's scanner picks them up. `top-30` is not on the named
 * scale, but v4 generates spacing utilities dynamically from `--spacing`.
 */
export const STICKY_CHROME_CLASS = {
  /** The top bar: menu, search, dialer, account. */
  topBar: 'sticky top-0',
  /** The page header bar — on a person, the name and avatar. */
  pageHeader: 'sticky top-8 lg:top-0 menu-nav:top-8',
  /** The person's nav select and toolbar, under the name. */
  personToolbar: 'sticky top-20 lg:top-12 menu-nav:top-20',
  /** Whatever a page pins below all of that, e.g. a filter row above a list. */
  pageSection: 'sticky top-30 lg:top-22 menu-nav:top-30'
} as const;

/**
 * An alert dialog has to be seen, so it paints above the whole app — including
 * the active call pill, which is fixed at `z-50` and would otherwise cover a
 * dialog sitting on the `z-30` overlay layer.
 *
 * A minimized alert sits just below the dialog itself and above the call pill.
 */
export const ALERT_DIALOG_Z_CLASS = 'z-[70]';
export const MEMBER_JOIN_ALERT_BAR_Z_CLASS = 'z-[60]';

/**
 * Bottom offset that clears the active call overlay on small screens, where both
 * are anchored to the same corner. It is a heuristic, not a measurement: the call
 * pill grows with its content and its audio and keypad panels stack above it, so
 * a tall enough call UI can still reach this. Measuring the overlay, or rendering
 * both in one flex container, is the fix if that turns out to bite.
 */
export const ALERT_BAR_BOTTOM_CLASS = 'bottom-40';
